"use client";

import { useState, useEffect, useRef } from "react";
import { useMinimapWidth, usePianoHeight, usePlayheadDistance, useStudio } from "@/contexts/studio-context";
import { CountIn, type CountInPhase } from "@/components/ui/molecules/count-in";
import type { CountInEvent } from "@/lib/studio/components/count-in";

type BeatState = {
  beat: number;
  totalBeats: number;
  beatDuration: number;
};

const EXIT_DURATION = 300;

/**
 * Studio-connected count-in. Subscribes to Studio count-in events
 * and manages the visibility lifecycle (idle → active → exiting → idle).
 */
export function StudioCountIn() {
  const { studio } = useStudio();
  // Le chiffre se centre dans le couloir de lecture — du haut du roll à la
  // playhead, minimap exclue — et non sur la zone studio : centré sur elle, il
  // penchait vers la minimap et descendait vers le clavier. Les trois mesures
  // viennent de la vue Pixi, à chaque layout.
  const minimapWidth = useMinimapWidth();
  const pianoHeight = usePianoHeight();
  const playheadDistance = usePlayheadDistance();
  const [phase, setPhase] = useState<CountInPhase>("idle");
  const [beatState, setBeatState] = useState<BeatState>({ beat: 0, totalBeats: 4, beatDuration: 500 });
  const exitTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!studio) {
      return;
    }

    return studio.countIn.on((event: CountInEvent) => {
      switch (event.type) {
        case "start":
          clearTimeout(exitTimer.current);
          setBeatState({ beat: event.beat, totalBeats: event.totalBeats, beatDuration: event.beatDuration });
          setPhase("active");
          break;
        case "beat":
          setBeatState({ beat: event.beat, totalBeats: event.totalBeats, beatDuration: event.beatDuration });
          break;
        case "complete":
        case "cancel":
          setPhase("exiting");
          exitTimer.current = setTimeout(() => setPhase("idle"), EXIT_DURATION);
          break;
      }
    });
  }, [studio]);

  // Cleanup exit timer on unmount
  useEffect(() => () => clearTimeout(exitTimer.current), []);

  const handleSkip = () => studio?.countIn.skip();

  return (
    <CountIn
      phase={phase}
      beat={beatState.beat}
      totalBeats={beatState.totalBeats}
      beatDuration={beatState.beatDuration}
      insetRight={minimapWidth}
      insetBottom={pianoHeight + playheadDistance}
      onSkip={handleSkip}
    />
  );
}
